// AP-168 · Portal do Cliente — "Meus Tickets"
// Fase 2 (AP-168.2): resposta rápida + "Problema resolvido".
// Totalmente localizado via usePortalI18n.
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { portalApi } from "../../context/PortalAuthContext";
import { usePortalNotifications } from "../../context/PortalNotificationsContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft, LifeBuoy, Clock, CheckCircle2, Loader2, MessageSquare, Send, User as UserIcon,
} from "lucide-react";
import { usePortalI18n } from "./PortalI18nContext";

const STATUS_STYLE = {
  open:        { bg: "bg-sky-50 text-sky-700 border-sky-200",         dot: "bg-sky-500",     labelKey: "tickets.status.open" },
  in_progress: { bg: "bg-amber-50 text-amber-700 border-amber-200",   dot: "bg-amber-500",   labelKey: "tickets.status.in_progress" },
  resolved:    { bg: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", labelKey: "tickets.status.resolved" },
  closed:      { bg: "bg-slate-50 text-slate-600 border-slate-200",   dot: "bg-slate-400",   labelKey: "tickets.status.closed" },
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export function PortalMyTickets() {
  const { t } = usePortalI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await portalApi.get("/portal/tickets");
      setItems(data?.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="portal-tickets" className="space-y-5">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-display font-bold flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-primary" /> {t("tickets.title")}
          </h2>
          <p className="text-xs text-muted-foreground">{t("tickets.subtitle")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate("/portal/assistente")}
                data-testid="portal-tickets-open-assistant">
          {t("tickets.openAssistant")}
        </Button>
      </header>

      {loading && (
        <div className="rounded-2xl border bg-white p-6 text-center text-sm text-muted-foreground"
             data-testid="portal-tickets-loading">
          <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> {t("common.loading")}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="rounded-2xl border bg-white p-8 text-center" data-testid="portal-tickets-empty">
          <LifeBuoy className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-sm font-semibold">{t("tickets.emptyTitle")}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("tickets.emptyHint")}</p>
          <Button className="mt-4" size="sm" onClick={() => navigate("/portal/assistente")}
                  data-testid="portal-tickets-empty-cta">
            {t("tickets.emptyCta")}
          </Button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <ul className="space-y-2" data-testid="portal-tickets-list">
          {items.map((it) => {
            const st = STATUS_STYLE[it.status] || STATUS_STYLE.open;
            return (
              <li key={it.id}>
                <button
                  onClick={() => navigate(`/portal/tickets/${it.id}`)}
                  data-testid={`portal-ticket-row-${it.id}`}
                  className="w-full text-left flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-border bg-white px-4 py-3 hover:border-primary/40 hover:shadow-sm transition-all active:scale-[0.99]"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[11px] font-semibold text-primary"
                            data-testid={`portal-ticket-number-${it.id}`}>
                        #{it.ticket_number}
                      </span>
                      <Badge className={`border ${st.bg}`} variant="outline">
                        <span className={`inline-block h-2 w-2 rounded-full mr-1.5 ${st.dot}`} />
                        {t(st.labelKey)}
                      </Badge>
                      {it.has_unread_reply && (
                        <Badge className="bg-rose-500 text-white">
                          <MessageSquare className="h-3 w-3 mr-1" /> {t("tickets.newReply")}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm font-medium mt-1 truncate">{it.subject}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {t("tickets.openedAt")} {fmtDateTime(it.created_at)} ·{" "}
                      {t("tickets.repliesCount").replace("{n}", String(it.reply_count || 0))}
                    </p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function PortalTicketDetail() {
  const { tid } = useParams();
  const { t } = usePortalI18n();
  const navigate = useNavigate();
  const { refresh: refreshNotifs } = usePortalNotifications();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // AP-168.2 — resposta rápida
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  // AP-168.2 — fechar
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeNote, setCloseNote] = useState("");
  const [closing, setClosing] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await portalApi.get(`/portal/tickets/${tid}`);
      setData(r.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || t("tickets.loadError"));
    } finally { if (!silent) setLoading(false); }
  };
  useEffect(() => { load(); try { refreshNotifs && refreshNotifs(); } catch {} /* eslint-disable-line */ }, [tid]);

  const sendReply = async () => {
    const text = replyText.trim();
    if (!text) { toast.error(t("tickets.replyEmpty")); return; }
    setSending(true);
    try {
      await portalApi.post(`/portal/tickets/${tid}/reply`, { body: text });
      setReplyText("");
      toast.success(t("tickets.replyOk"));
      await load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("tickets.replyFail"));
    } finally { setSending(false); }
  };

  const closeTicket = async () => {
    setClosing(true);
    try {
      await portalApi.post(`/portal/tickets/${tid}/close`, { note: closeNote.trim() || null });
      toast.success(t("tickets.closeOk"));
      setCloseOpen(false); setCloseNote("");
      await load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("tickets.closeFail"));
    } finally { setClosing(false); }
  };

  if (loading) {
    return (
      <div data-testid="portal-ticket-loading"
           className="rounded-2xl border bg-white p-6 text-center text-sm text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mx-auto" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-2xl border bg-white p-6 text-center" data-testid="portal-ticket-error">
        <p className="text-sm text-muted-foreground">{error || t("tickets.notFound")}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate("/portal/tickets")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> {t("detail.back")}
        </Button>
      </div>
    );
  }

  const st = STATUS_STYLE[data.status] || STATUS_STYLE.open;
  const snapshot = data.conversation_snapshot || [];
  const replies = data.replies || [];
  const isResolved = ["resolved", "closed"].includes(data.status);
  const canClose = !isResolved;

  return (
    <div data-testid="portal-ticket-detail" className="space-y-5">
      <header className="rounded-2xl border bg-white p-5">
        <Button variant="outline" size="sm" onClick={() => navigate("/portal/tickets")}
                data-testid="portal-ticket-back" className="mb-3">
          <ArrowLeft className="h-4 w-4 mr-1" /> {t("detail.back")}
        </Button>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-primary font-semibold">#{data.ticket_number}</span>
          <Badge className={`border ${st.bg}`} variant="outline" data-testid="portal-ticket-status-badge">
            <span className={`inline-block h-2 w-2 rounded-full mr-1.5 ${st.dot}`} />
            {t(st.labelKey)}
          </Badge>
        </div>
        <h2 className="mt-1 text-lg font-display font-bold">{data.subject}</h2>
        <p className="text-xs text-muted-foreground">
          {t("tickets.openedAt")} {fmtDateTime(data.created_at)} ·{" "}
          {t("tickets.lastUpdate")} {fmtDateTime(data.updated_at)}
        </p>
        {canClose && (
          <div className="mt-3">
            <Button size="sm" variant="outline"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => setCloseOpen(true)}
                    data-testid="portal-ticket-close">
              {t("tickets.closeBtn")}
            </Button>
          </div>
        )}
      </header>

      {isResolved && (
        <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900"
             data-testid="portal-ticket-resolved-banner">
          {t("tickets.resolvedBanner")}
        </div>
      )}

      {snapshot.length > 0 && (
        <section className="rounded-2xl border bg-white p-5" data-testid="portal-ticket-snapshot">
          <h3 className="text-sm font-semibold mb-3">{t("tickets.snapshot")}</h3>
          <ol className="space-y-2">
            {snapshot.map((m, i) => (
              <li key={i} className={`rounded-xl px-3 py-2 text-sm ${
                m.role === "user" ? "bg-primary/5 border border-primary/20"
                                   : "bg-slate-50 border border-slate-200"}`}>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">
                  {m.role === "user" ? t("tickets.you") : t("tickets.ai")}
                </p>
                <p className="whitespace-pre-wrap">{m.content}</p>
              </li>
            ))}
          </ol>
        </section>
      )}

      <section className="rounded-2xl border bg-white p-5" data-testid="portal-ticket-replies">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
          <MessageSquare className="h-4 w-4" /> {t("tickets.replies")}
        </h3>
        {replies.length === 0 ? (
          <div className="rounded-xl border border-dashed p-4 text-center text-sm text-muted-foreground">
            <Clock className="h-5 w-5 mx-auto mb-2 opacity-50" />
            <p>{t("tickets.awaitingReply")}</p>
          </div>
        ) : (
          <ol className="space-y-3">
            {replies.map((r) => {
              const isClient = r.author_type === "client";
              return (
                <li key={r.id}
                    className={`rounded-xl border p-3 ${
                      isClient ? "bg-primary/5 border-primary/30"
                                : "bg-emerald-50/40 border-emerald-200"}`}
                    data-testid={`portal-ticket-reply-${r.id}`}>
                  <div className="flex items-center justify-between gap-2 text-[11px]">
                    <span className={`font-semibold flex items-center gap-1 ${
                      isClient ? "text-primary" : "text-emerald-800"}`}>
                      {isClient ? <UserIcon className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                      {isClient ? t("tickets.youLabel") : r.author_name}
                    </span>
                    <span className="text-muted-foreground">{fmtDateTime(r.at)}</span>
                  </div>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{r.body}</p>
                </li>
              );
            })}
          </ol>
        )}

        {/* AP-168.2 · Resposta rápida pelo próprio Portal */}
        <div className="mt-4 pt-4 border-t space-y-2" data-testid="portal-ticket-reply-form">
          <Label className="text-xs">{t("tickets.replySend")}</Label>
          <Textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder={t("tickets.replyPlaceholder")}
            rows={3}
            disabled={sending}
            data-testid="portal-ticket-reply-textarea"
            className="text-sm resize-none"
          />
          <div className="flex items-center justify-end gap-2">
            <Button onClick={sendReply} disabled={sending || !replyText.trim()}
                    data-testid="portal-ticket-reply-send">
              {sending
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> {t("tickets.replySending")}</>
                : <><Send className="h-3.5 w-3.5 mr-1" /> {t("tickets.replySend")}</>}
            </Button>
          </div>
        </div>
      </section>

      {/* AP-168.2 · Confirmação de "Problema resolvido" */}
      <AlertDialog open={closeOpen} onOpenChange={setCloseOpen}>
        <AlertDialogContent data-testid="portal-ticket-close-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-emerald-800">{t("tickets.closeConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>{t("tickets.closeConfirmDesc")}</p>
                <div>
                  <Label className="text-xs">{t("tickets.closeNoteLabel")}</Label>
                  <Textarea rows={2} value={closeNote}
                            onChange={(e) => setCloseNote(e.target.value)}
                            placeholder={t("tickets.closeNotePh")}
                            data-testid="portal-ticket-close-note"
                            className="text-sm resize-none mt-1" />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={closing}>{t("cancel") || "Cancelar"}</AlertDialogCancel>
            <AlertDialogAction disabled={closing} onClick={closeTicket}
                                className="bg-emerald-600 hover:bg-emerald-700"
                                data-testid="portal-ticket-close-confirm">
              {closing
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> {t("tickets.replySending")}</>
                : t("tickets.closeConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
